
import React, { useState } from 'react';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const faqs = [
    {
      question: 'What is NAD+ and why is it important?',
      answer: 'NAD+ (Nicotinamide Adenine Dinucleotide) is a crucial coenzyme found in every cell of your body. It plays a vital role in energy production, DNA repair, and cellular communication. As we age, NAD+ levels naturally decline by up to 50% by age 40, leading to decreased energy, cognitive decline, and accelerated aging. NAD+ therapy replenishes these levels to restore optimal cellular function.'
    },
    {
      question: 'How does NAD+ therapy work?',
      answer: 'NAD+ therapy works by directly replenishing cellular NAD+ levels through IV infusion, injection, or other delivery methods. Once in your system, NAD+ activates sirtuins (longevity proteins), enhances mitochondrial function, supports DNA repair mechanisms, and optimizes cellular energy production. This leads to improved energy, mental clarity, and overall cellular health.'
    },
    {
      question: 'What can I expect during an NAD+ IV treatment?',
      answer: 'NAD+ IV treatments typically take 2-4 hours in our comfortable treatment rooms. You\'ll relax in a reclining chair while the NAD+ is slowly infused. Some patients experience mild flushing, warmth, or slight nausea initially, which typically subsides as your body adjusts. Most patients feel energized and mentally clear within hours of treatment.'
    },
    {
      question: 'How quickly will I see results from NAD+ therapy?',
      answer: 'Many patients notice immediate benefits within 24-48 hours, including increased energy, improved mental clarity, and better mood. Optimal benefits typically develop over 1-2 weeks with sustained improvements continuing throughout your treatment series. Long-term benefits like improved sleep, enhanced recovery, and anti-aging effects become more pronounced with regular treatments.'
    },
    {
      question: 'Is NAD+ therapy safe?',
      answer: 'NAD+ therapy is extremely safe when administered by qualified medical professionals. NAD+ is a naturally occurring molecule in your body, so adverse reactions are rare. The most common side effects are mild and temporary, including slight nausea, flushing, or fatigue during treatment. We monitor all patients throughout their treatment to ensure safety and comfort.'
    },
    {
      question: 'How often should I receive NAD+ treatments?',
      answer: 'Treatment frequency depends on your individual goals and health status. For general wellness and anti-aging, monthly treatments are often sufficient. For specific health concerns or optimization goals, weekly or bi-weekly treatments may be recommended initially, followed by maintenance treatments. Our medical team will create a personalized schedule based on your needs.'
    },
    {
      question: 'Can NAD+ therapy help with addiction recovery?',
      answer: 'Yes, NAD+ therapy has shown significant benefits in addiction recovery by helping restore neurotransmitter balance, reducing cravings, and supporting brain healing. NAD+ helps repair the neurological damage caused by substance abuse and can significantly ease withdrawal symptoms. Many addiction treatment centers now incorporate NAD+ therapy as part of comprehensive recovery programs.'
    },
    {
      question: 'What is the difference between NAD+ IV therapy and oral supplements?',
      answer: 'NAD+ IV therapy provides direct, immediate delivery to your bloodstream with 100% bioavailability, while oral NAD+ supplements have poor absorption and are largely broken down in the digestive system. IV therapy can increase NAD+ levels by 500% or more, while oral supplements typically provide minimal increases. For therapeutic benefits, IV therapy is significantly more effective.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6">
            <strong>NAD+ Therapy</strong> FAQ
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
            Get answers to the most common questions about NAD+ therapy and how it can transform your health.
          </p>
        </div>

        <div className="space-y-4 lg:space-y-6">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-gray-50 rounded-2xl overflow-hidden">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 lg:px-8 py-4 lg:py-6 text-left flex justify-between items-center hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <h3 className="text-lg lg:text-xl font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <i className={`ri-${openFAQ === index ? 'subtract' : 'add'}-line text-2xl text-green-600 flex-shrink-0`}></i>
              </button>
              
              {openFAQ === index && (
                <div className="px-6 lg:px-8 pb-4 lg:pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 lg:mt-16 text-center">
          <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-6 lg:p-8 text-white">
            <h3 className="text-xl lg:text-2xl font-bold mb-4">
              Ready to Experience NAD+ Therapy?
            </h3>
            <p className="text-lg mb-6 opacity-90">
              Our NAD+ specialists are here to help you understand how this revolutionary therapy can optimize your health.
            </p>
            <button className="bg-white text-green-600 px-8 py-4 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Schedule Free Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
